import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextField } from "@/components/field";
import type { Staff } from "@/lib/types";
import { STAFF_ROLES, useCreateStaff, useSetStaffPassword } from "./queries";

export const passwordSchema = z
  .string()
  .min(12, "At least 12 characters")
  .max(128, "At most 128 characters");

const addSchema = z.object({
  email: z.email("Enter a valid email"),
  display_name: z.string().trim(),
  role: z.enum(STAFF_ROLES),
  password: z.union([z.literal(""), passwordSchema]),
});
type AddValues = z.infer<typeof addSchema>;

export function AddStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const create = useCreateStaff();
  const form = useForm<AddValues>({
    resolver: zodResolver(addSchema),
    defaultValues: { email: "", display_name: "", role: "support", password: "" },
  });
  const err = (k: keyof AddValues) => form.formState.errors[k]?.message;
  const close = (o: boolean) => {
    if (!o) form.reset();
    onOpenChange(o);
  };
  const onSubmit = form.handleSubmit(async (v) => {
    await create.mutateAsync({
      email: v.email,
      display_name: v.display_name || undefined,
      role: v.role,
      password: v.password || undefined,
    });
    close(false);
  });
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Add staff</DialogTitle>
            <DialogDescription>
              Staff sign in with Microsoft Entra when their email matches, or with a local password.
            </DialogDescription>
          </DialogHeader>
          <TextField
            label="Email"
            type="email"
            autoComplete="off"
            error={err("email")}
            {...form.register("email")}
          />
          <TextField label="Name" error={err("display_name")} {...form.register("display_name")} />
          <SelectField label="Role" error={err("role")} {...form.register("role")}>
            {STAFF_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Initial password (optional)"
            type="password"
            autoComplete="new-password"
            hint="Leave blank for Entra-only sign-in."
            error={err("password")}
            {...form.register("password")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Adding…" : "Add staff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const pwSchema = z
  .object({ new_password: passwordSchema, confirm: z.string() })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });
type PwValues = z.infer<typeof pwSchema>;

export function SetPasswordDialog({
  staff,
  onOpenChange,
}: {
  staff: Staff | null;
  onOpenChange: (o: boolean) => void;
}) {
  const set = useSetStaffPassword();
  const form = useForm<PwValues>({
    resolver: zodResolver(pwSchema),
    defaultValues: { new_password: "", confirm: "" },
  });
  const err = (k: keyof PwValues) => form.formState.errors[k]?.message;
  const close = (o: boolean) => {
    if (!o) form.reset();
    onOpenChange(o);
  };
  const onSubmit = form.handleSubmit(async (v) => {
    if (!staff) return;
    await set.mutateAsync({ id: staff.id, new_password: v.new_password });
    close(false);
  });
  return (
    <Dialog open={staff !== null} onOpenChange={close}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Set password for {staff?.email}</DialogTitle>
            <DialogDescription>
              Replaces any existing local password. Tell them out of band.
            </DialogDescription>
          </DialogHeader>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            error={err("new_password")}
            {...form.register("new_password")}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={err("confirm")}
            {...form.register("confirm")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={set.isPending}>
              {set.isPending ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
